import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import DenyAdmissionModal from 'components/modals/admission/DenyAdmissionModal';
import DeleteAdmissionModal from 'components/modals/DeleteAdmissionModal';
import EditAdmissionPatientModal from 'components/modals/EditAdmissionPatientModal';
import AIButton from 'components/shared/ai/AIButton';
import CircularProgress from 'components/shared/CircularProgress';
import CloseButton from 'components/shared/CloseButton';
import Foldout from 'components/shared/Foldout';
import { SupportEmailLink } from 'components/shared/SupportEmailLink';
import Tabs, { TabType } from 'components/Tabs';
import { colors } from 'styles/theme/colors';
import SummaryFailedIcon from 'svg/SummaryFailedIcon';

import AdmissionCriteriaTab from './AdmissionCriteriaTab';
import AdmissionFilesTab from './AdmissionFilesTab';
import AdmissionLoadingState from './AdmissionLoadingState';
import AdmissionSlideoutHeader from './AdmissionSlideoutHeader';
import AdmissionSummaryTab from './AdmissionSummaryTab';
import useAdmissionSlideout from './useAdmissionSlideout';

export default function AdmissionSlideout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    admissionId,
    admission,
    showFullLoading,
    hasMultiplePatientsError,
    criteriaEvaluationJobErrored,
    handleInitialLoadingDone,
    attachmentsCount,
  } = useAdmissionSlideout();

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => navigate('/admissions'), 500);
  };

  const tabs = [
    { value: 'criteria', label: 'Admission Criteria', to: `/admissions/${admissionId}/criteria` },
    { value: 'summary', label: 'Summary', to: `/admissions/${admissionId}/summary` },
    {
      value: 'files',
      label: attachmentsCount !== undefined ? `Files (${attachmentsCount})` : 'Files',
      to: `/admissions/${admissionId}/files`,
    },
  ] as const satisfies readonly TabType[];

  const activeTab = tabs.find((tab) => location.pathname.endsWith(`/${tab.value}`))?.value ?? 'criteria';

  const closeButton = <PositionedClose onClick={handleClose} />;

  const renderTakeover = (message: ReactNode) => (
    <>
      <HeaderCloseOnly>{closeButton}</HeaderCloseOnly>
      <ErrorContainer>
        <SummaryFailedIcon />
        <ErrorText>
          <strong>Admissions criteria failed to process.</strong> {message}
        </ErrorText>
        <StartNewButtonWrapper>
          <AIButton onClick={handleClose} fullWidth>
            Start New Admission Summary
          </AIButton>
        </StartNewButtonWrapper>
      </ErrorContainer>
    </>
  );

  const renderContent = () => {
    if (!admission) {
      return (
        <LoadingContainer>
          {closeButton}
          <SpinnerContainer>
            <CircularProgress color={colors.blue100} />
          </SpinnerContainer>
        </LoadingContainer>
      );
    }

    if (hasMultiplePatientsError) {
      return renderTakeover(
        <>
          Multiple patients were identified in the source documentation. Documentation must be specific to a single
          patient.
        </>
      );
    }

    if (criteriaEvaluationJobErrored) {
      return renderTakeover(
        <>
          Something went wrong. Click below to start a new admission summary or contact <SupportEmailLink />.
        </>
      );
    }

    if (showFullLoading) {
      return (
        <>
          <HeaderCloseOnly>{closeButton}</HeaderCloseOnly>
          <Content>
            <AdmissionLoadingState admission={admission} onLoadingFinished={handleInitialLoadingDone} />
          </Content>
        </>
      );
    }

    return (
      <>
        <AdmissionSlideoutHeader
          admission={admission}
          onDeny={() => setShowDenyModal(true)}
          onEditPatient={() => setShowEditPatientModal(true)}
          onDeleteSummary={() => setShowDeleteModal(true)}
          closeButton={closeButton}
        />

        <TabBar>
          <Tabs tabs={tabs} />
        </TabBar>

        <Content>
          {activeTab === 'criteria' && <AdmissionCriteriaTab admission={admission} />}
          {activeTab === 'summary' && <AdmissionSummaryTab admission={admission} />}
          {activeTab === 'files' && <AdmissionFilesTab reviewId={admission.id} />}
        </Content>
      </>
    );
  };

  return (
    <>
      <Foldout isNested open={isOpen} width='min(100%, 1200px)' onBackdropClick={handleClose}>
        <Container>{renderContent()}</Container>
      </Foldout>

      {admission && showDenyModal && (
        <ModalWrapper>
          <DenyAdmissionModal admission={admission} onCancel={() => setShowDenyModal(false)} />
        </ModalWrapper>
      )}

      {admission && showEditPatientModal && (
        <ModalWrapper>
          <EditAdmissionPatientModal admission={admission} onCancel={() => setShowEditPatientModal(false)} />
        </ModalWrapper>
      )}

      {admission && showDeleteModal && (
        <ModalWrapper>
          <DeleteAdmissionModal admission={admission} onCancel={() => setShowDeleteModal(false)} />
        </ModalWrapper>
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
`;

const SpinnerContainer = styled.div`
  background-color: ${colors.black25};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100px;
`;

const HeaderCloseOnly = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 0;
  position: relative;
`;

const PositionedClose = styled(CloseButton)`
  position: absolute;
  right: 24px;
  top: 16px;

  &:hover {
    color: ${colors.black100};
  }
`;

const TabBar = styled.div`
  // Pairs with 10px top padding on each tab item for 24px total spacing
  padding: 14px 40px 0;
  border-bottom: 1px solid ${colors.black10};
`;

const Content = styled.div`
  flex: 1;
  padding: 24px 40px 40px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const ModalWrapper = styled.div`
  position: relative;
  z-index: 1200;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  align-items: center;
  gap: 32px;
  max-width: 640px;
  margin-top: 60px;
  padding: 0 40px;
  text-align: center;
`;

const ErrorText = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

const StartNewButtonWrapper = styled.div`
  width: 240px;
`;
